<template>
  <ValidationObserver ref="observer">
    <v-row>
      <v-col>
        <VTextField
          v-model="data.name[selectedLang]"
          :label="$t(`forms.labels.name_${selectedLang}`)"
          :rules="{ required: selectedLang === teamMainLang, min: 1, max: 200 }"
          outlined
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="4"
      >
        <VTextField
          v-model="data.ERPCode"
          :label="$t('forms.labels.ERPCode')"
          :rules="{ min: 1, max: 200 }"
          outlined
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <VAutocomplete
          ref="autocompleteRef"
          v-model="data.types"
          multiple
          outlined
          :items="displayedTypes"
          :label="$t('forms.labels.analysisTypes')"
          :loading="loadingTypes"
          :disabled="selectedLang !== teamMainLang"
          :rules="{ required: true }"
          validateAtInitialization
          data-cy="AnalysisGroupType"
          @search="search"
          @change="onChange"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersect" />
          </template>
        </VAutocomplete>
      </v-col>
    </v-row>
  </ValidationObserver>
</template>

<script>
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { isEmpty } from 'lodash';

export default {
    components: {
        ValidationObserver,
        VAutocomplete,
        VTextField,
    },
    props: {
        group: {
            type: Object,
            default: () => ({ empty: true }),
        },
        isEdition: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: {
                name: {},
                types: [],
            },
            availableTypes: [],
            loadingTypes: false,
            pagination: {},
            queryParams: { perPage: 25 },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
            'selectedLang',
        ]),
        displayedTypes() {
            return this.availableTypes.map(type => ({
                id: type.id,
                name: type.name[this.userLang] && type.name[this.userLang].length
                    ? type.name[this.userLang] : type.name[this.teamMainLang],
            }));
        },
    },
    watch: {
        group: {
            handler() {
                if (this.isEdition) {
                    this.data = this.group;
                }
            },
            immediate: true,
        },
        data: {
            handler() {
                this.updateData();
            },
            deep: true,
        },
    },
    async created() {
        await this.getAnalysisTypes();
    },
    methods: {
        async getAnalysisTypes() {
            this.loadingTypes = true;
            if (this.isEdition) {
                const { data } = await this.$rq.getAnalysisTypes({ typeName: 'All', pagination: false });
                this.availableTypes = data.types;
            } else if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getAnalysisTypes(this.queryParams);
                this.availableTypes = [...this.availableTypes, ...data.types];
                this.pagination = data.pagination;
            }
            this.loadingTypes = false;
        },
        updateData() {
            this.$emit('sendData', this.data);
        },
        async search(search) {
            const { data } = await this.$rq.getAnalysisTypes({ search, pagination: false, typeName: 'All' });
            this.availableTypes = data.types;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getAnalysisTypes();
                // access "v-autocomplete" ref
                const ref = this.$refs?.autocompleteRef?.$refs?.autocomplete;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        async onChange(value) {
            // on clear the search
            if (!value) {
                this.pagination = {};
                this.queryParams = {};
                this.availableTypes = [];
                await this.getAnalysisTypes();
            }
        },
    },
};
</script>
